import { Button } from "@repo/ui";
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { usePrintCoupons } from "@/lib/queries/use-print-coupons";

// Configuration constants for coupon positioning
// Adjust these values after printing test pages and measuring alignment
const COUPON_CONFIG = {
  WIDTH: "10.5cm",
  HEIGHT: "29.7cm",
  TOP_SECTION_OFFSET: "3.55cm", // Starting position for first section from top
  BOTTOM_SECTION_OFFSET: "13.35cm", // Starting position for second section from top
  LEFT_MARGIN: "1.0cm", // Left offset for all fields
  LINE_HEIGHT: "0.4cm", // Line height for text
  FIELD_HEIGHT: "0.8cm", // Height of each field area (2 lines worth)
  FIELD_SPACING: "0.85cm", // Spacing between different fields
  MAX_WIDTH: "8.5cm", // Maximum width for text fields
  FONT_SIZE: "14px", // Base font size
  THB_AMOUNT_PER_COUPON: 300000,
} as const;

// Format phone number as 000-000-0000
const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, "");

  // Format as 000-000-0000
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  // Return original if not 10 digits
  return phone;
};

export default function PrintCouponPage() {
  const { propertyPurchaseId } = useParams();
  const hasFetchedCoupons = useRef(false);
  const { mutate, data, isPending, error } = usePrintCoupons(propertyPurchaseId);

  useEffect(() => {
    document.title = "Print Coupon | House & Condo Expo";
  }, []);

  useEffect(() => {
    // Reset flag when propertyPurchaseId changes
    hasFetchedCoupons.current = false;
  }, [propertyPurchaseId]);

  useEffect(() => {
    // Fetch and print coupons for this property purchase
    if (!propertyPurchaseId || hasFetchedCoupons.current) return;

    hasFetchedCoupons.current = true;
    mutate();
  }, [propertyPurchaseId, mutate]);

  const handlePrint = () => {
    window.print();
  };

  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-destructive">{error.message}</p>
      </div>
    );
  }

  if (!data?.property_purchase || !data?.registration) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Property purchase not found</p>
      </div>
    );
  }

  const { coupons, property_purchase: propertyPurchase, registration } = data;

  const valueAmount = propertyPurchase.purchase_price;
  const numCoupons = Math.round(valueAmount / COUPON_CONFIG.THB_AMOUNT_PER_COUPON);

  if (numCoupons === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>No coupons available for this property purchase</p>
      </div>
    );
  }

  // Wait for coupons to be loaded from API
  if (coupons.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading coupons...</p>
      </div>
    );
  }

  // Helper function to render a section of fields at a specific vertical offset
  const renderSection = (topOffset: string) => {
    let currentTop = topOffset;
    const fields = [
      `${registration.name} ${registration.surname}`, // Name
      propertyPurchase.full_address || "-", // Full Address
      formatPhoneNumber(registration.phone), // Phone
      propertyPurchase.developer || "-", // Developer
    ];

    return fields.map((fieldValue, fieldIndex) => {
      const element = (
        <div
          key={fieldIndex}
          className="coupon-field"
          style={{
            position: "absolute",
            top: currentTop,
            left: COUPON_CONFIG.LEFT_MARGIN,
            maxWidth: COUPON_CONFIG.MAX_WIDTH,
            height: COUPON_CONFIG.FIELD_HEIGHT,
            fontSize: COUPON_CONFIG.FONT_SIZE,
            lineHeight: COUPON_CONFIG.LINE_HEIGHT,
          }}
        >
          {fieldValue}
        </div>
      );

      // Calculate next position (moving down the page)
      currentTop = `calc(${currentTop} + ${COUPON_CONFIG.FIELD_HEIGHT} + ${COUPON_CONFIG.FIELD_SPACING})`;

      return element;
    });
  };

  return (
    <>
      <style>{`
        .coupon-field {
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          overflow-wrap: break-word;
          word-wrap: break-word;
          overflow: hidden;
        }
        @media print {
          * {
            margin: 0 !important;
            padding: 0 !important;
          }
          body, html {
            margin: 0 !important;
            padding: 0 !important;
            height: auto;
          }
          .coupon-page {
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
            position: relative;
            margin: 0 !important;
            padding: 0 !important;
          }
          .coupon-page:last-child {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          .no-print {
            display: none !important;
          }
          .coupon-field {
            max-width: ${COUPON_CONFIG.MAX_WIDTH};
            display: flex;
          }
        }
        @page {
          size: ${COUPON_CONFIG.WIDTH} ${COUPON_CONFIG.HEIGHT};
          margin: 0;
        }
      `}</style>

      <div className="no-print p-4 bg-muted border-b flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Ready to print {coupons.length} coupon{coupons.length > 1 ? "s" : ""} for{" "}
          {registration.name} {registration.surname}
          {coupons.length > 0 && (
            <span className="ml-2">
              (Coupon #{coupons[0].couponNumber}
              {coupons.length > 1 && ` - #${coupons[coupons.length - 1].couponNumber}`})
            </span>
          )}
          <span className="ml-2">
            - {propertyPurchase.developer} - ฿{propertyPurchase.purchase_price.toLocaleString()}
          </span>
        </p>
        <Button onClick={handlePrint} className="ml-4">
          Print Coupons
        </Button>
      </div>

      {coupons.map((coupon) => (
        <div
          key={coupon.uuid}
          className="coupon-page"
          style={{
            width: COUPON_CONFIG.WIDTH,
            height: COUPON_CONFIG.HEIGHT,
          }}
        >
          {/* Display coupon number in top right corner */}
          <div
            style={{
              position: "absolute",
              top: "0.5cm",
              right: "0.5cm",
              fontSize: "16px",
              fontWeight: "bold",
              color: "#000",
            }}
          >
            #{coupon.couponNumber}
          </div>

          {/* Top section */}
          {renderSection(COUPON_CONFIG.TOP_SECTION_OFFSET)}

          {/* Display coupon number in bottom section header */}
          <div
            style={{
              position: "absolute",
              top: "calc(13.35cm - 0.5cm)", // Just above the bottom section
              right: "0.5cm",
              fontSize: "16px",
              fontWeight: "bold",
              color: "#000",
            }}
          >
            #{coupon.couponNumber}
          </div>

          {/* Bottom section */}
          {renderSection(COUPON_CONFIG.BOTTOM_SECTION_OFFSET)}
        </div>
      ))}
    </>
  );
}
